import React, { createContext, useContext, useMemo } from "react";
import { colors as defaultColors } from "./colors";

// widen literal palette to accept arbitrary overrides (string/number)
type WidenPrimitive<T> = T extends string
  ? string
  : T extends number
    ? number
    : T;
type DeepWiden<T> = {
  [K in keyof T]: T[K] extends object ? DeepWiden<T[K]> : WidenPrimitive<T[K]>;
};

export type SharedColorPalette = DeepWiden<typeof defaultColors>;

type SharedComponentsThemeContextValue = {
  colors: SharedColorPalette;
};

const SharedComponentsThemeContext =
  createContext<SharedComponentsThemeContextValue>({
    colors: defaultColors as SharedColorPalette,
  });

// 非破壊の簡易ディープマージ（2階層までで十分）
function mergeColors(
  base: SharedColorPalette,
  override?: Partial<SharedColorPalette>
): SharedColorPalette {
  if (!override) return base;
  const result: any = { ...base };
  for (const key of Object.keys(override) as Array<keyof SharedColorPalette>) {
    const baseSection = (base as any)[key];
    const overrideSection = (override as any)[key];
    if (
      overrideSection &&
      typeof overrideSection === "object" &&
      !Array.isArray(overrideSection)
    ) {
      result[key] = { ...baseSection, ...overrideSection };
    } else if (overrideSection !== undefined) {
      result[key] = overrideSection;
    }
  }
  return result as SharedColorPalette;
}

export const SharedComponentsThemeProvider: React.FC<{
  children: React.ReactNode;
  colorsOverride?: Partial<SharedColorPalette> | SharedColorPalette;
}> = ({ children, colorsOverride }) => {
  const merged = useMemo(
    () =>
      mergeColors(defaultColors as SharedColorPalette, colorsOverride as any),
    [colorsOverride]
  );

  const value = useMemo<SharedComponentsThemeContextValue>(
    () => ({ colors: merged }),
    [merged]
  );

  return (
    <SharedComponentsThemeContext.Provider value={value}>
      {children}
    </SharedComponentsThemeContext.Provider>
  );
};

export function useSharedColors(): SharedColorPalette {
  return useContext(SharedComponentsThemeContext).colors;
}
